import { chakra, Flex, Icon, Text } from '@chakra-ui/react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import React from 'react'
import { useTranslation } from 'react-i18next'

/** -1 steps to the previous banner, 1 to the next. */
export type BannerPagerStep = -1 | 1

interface BannerPagerProps {
  /** 1-based position of the visible banner among the banners shown. */
  position: number
  total: number
  onStep: (step: BannerPagerStep) => void
}

// The ring is drawn inside so the narrow, clipped rail can't cut it off.
const PagerButton = chakra('button', {
  base: {
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    cursor: 'pointer',
    borderRadius: 'sm',
    _hover: { bg: 'bg.muted' },
    focusVisibleRing: 'inside',
    focusRingColor: 'brand.fg',
  },
})

/**
 * Vertical previous / next rail beside the stacked banners. Uses its own
 * neutral surface so it reads the same next to every banner background.
 */
export const BannerPager = ({ position, total, onStep }: BannerPagerProps) => {
  const { t } = useTranslation()

  return (
    <Flex
      direction="column"
      alignItems="center"
      flexShrink={0}
      width="40px"
      padding="2px"
      bg="bg.panel"
      color="fg.default"
      borderRightWidth="1px"
      borderBottomWidth="1px"
      borderColor="border"
    >
      <PagerButton
        type="button"
        aria-label={t('bannerPager.previous', 'Previous banner')}
        onClick={() => onStep(-1)}
      >
        <Icon boxSize="16px" aria-hidden>
          <ChevronUp size="100%" />
        </Icon>
      </PagerButton>
      <Text
        aria-hidden
        fontSize="12px"
        fontWeight="600"
        lineHeight="16px"
        whiteSpace="nowrap"
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {t('bannerPager.position', '{{current}}/{{total}}', {
          current: position,
          total,
        })}
      </Text>
      <PagerButton
        type="button"
        aria-label={t('bannerPager.next', 'Next banner')}
        onClick={() => onStep(1)}
      >
        <Icon boxSize="16px" aria-hidden>
          <ChevronDown size="100%" />
        </Icon>
      </PagerButton>
    </Flex>
  )
}
